import { useSyncExternalStore } from 'react'
import YAML from 'yaml'
import { invariant } from '../lib/invariant'
import { DocumentStore } from './DocumentStore'
import { FileDocument } from './FileDocument'

export function useJSONDocumentData(document: JSONDocument) {
  return useSyncExternalStore(
    (callback) => {
      document.addListener(callback)
      return () => document.removeListener(callback)
    },
    () => {
      document.reloadContent().catch(console.error)
      return document.data
    }
  )
}

export type JSONDocumentFormat = 'json' | 'yaml'

// JSONDocument can be serialized to YAML!
export class JSONDocument extends FileDocument {
  format: JSONDocumentFormat
  constructor(store: DocumentStore, path: string, fileHandle: FileSystemFileHandle, format: JSONDocumentFormat) {
    super(store, path, fileHandle)
    this.format = format
  }

  data: any | undefined
  jsonError: Error | undefined

  override async updateText(text: string) {
    this.data = undefined
    this.jsonError = undefined

    await super.updateText(text)
  }

  async updateData(data: any) {
    const text = this.format === 'json' ? JSON.stringify(data, null, '  ') : YAML.stringify(data)
    await this.updateText(text)
  }

  override error(): Error | undefined {
    return this.jsonError ?? super.error()
  }

  async reloadContent() {
    if (this.data !== undefined || this.error() !== undefined) {
      return
    }

    await super.reloadContent()
    if (this.text === undefined) {
      return
    }

    try {
      switch (this.format) {
        case 'json':
          this.data = JSON.parse(this.text)
          this.jsonError = undefined
          break
        case 'yaml':
          this.data = YAML.parse(this.text)
          this.jsonError = undefined
          break
        default:
          const _: never = this.format
          invariant(false, `Invalid JSON document format '${this.format}'`)
      }
    } catch (error) {
      this.data = undefined
      this.jsonError = new Error(`Unable to parse ${this.format.toUpperCase()} document`, { cause: error })
    }

    this.dispatchChange()
  }
}
